import useChatSummariesReconciliation from 'hooks/chat/useChatSummariesReconciliation';
import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import chatService from 'services/chat';
import { buildAssignmentChatFilter } from 'utils/chat';

const listAssignmentChatsMap = async ({ assignmentIds, courseId, ownerAccountId }) => {
    const assignmentChats = await chatService.listChats(buildAssignmentChatFilter({ courseId, ownerAccountId }), {
        includeMessages: false,
    });

    const assignmentIdsSet = new Set(assignmentIds.map(String));

    return assignmentChats.reduce((result, assignmentChat) => {
        if (!assignmentIdsSet.has(String(assignmentChat.assignment_id))) return result;
        result[assignmentChat.assignment_id] = assignmentChat;
        return result;
    }, {});
};

/**
 * Summary-level chat loader for many assignments.
 *
 * Use this in assignment list/card views that need unread/latest-chat indicators for multiple assignments.
 * It keeps a map keyed by assignment id and refreshes individual summaries after completion or reconciliation drift.
 * It intentionally does not load full message pages; use `useAssignmentChatThread` for an opened chat.
 *
 * @param {Object} props
 * @param {Array<string|number>} props.assignmentIds Assignments to track.
 * @param {string} props.courseId Course that owns the assignments.
 * @param {string} props.ownerAccountId Account that owns the chats.
 * @returns {{assignmentChatsByAssignmentId: Object<string, Object>|null}} Summary map keyed by assignment id.
 */
const useAssignmentChatsMap = ({ assignmentIds, courseId, ownerAccountId }) => {
    const [assignmentChatsByAssignmentId, setAssignmentChatsByAssignmentId] = useState(null);

    const trackedAssignmentIdsKey = useMemo(() => (assignmentIds || []).join(','), [assignmentIds]);
    const trackedAssignmentIds = useMemo(
        () => (trackedAssignmentIdsKey ? trackedAssignmentIdsKey.split(',') : []),
        [trackedAssignmentIdsKey]
    );

    const refreshAssignmentChat = useCallback(
        async ({ assignment_id: assignmentId }) => {
            if (!assignmentId || !ownerAccountId || !courseId) return;

            const nextAssignmentChat = await chatService.getAssignmentChatSummary(
                buildAssignmentChatFilter({
                    assignmentId,
                    courseId,
                    ownerAccountId,
                })
            );

            setAssignmentChatsByAssignmentId((current) => {
                if (!current || !current[assignmentId]) return current;

                return {
                    ...current,
                    [assignmentId]: nextAssignmentChat,
                };
            });
        },
        [courseId, ownerAccountId]
    );

    useEffect(() => {
        if (!ownerAccountId || !courseId) {
            setAssignmentChatsByAssignmentId(null);
            return;
        }

        if (!trackedAssignmentIds.length) {
            setAssignmentChatsByAssignmentId({});
            return;
        }

        let isMounted = true;

        const loadAssignmentChatsMap = async () => {
            try {
                const nextAssignmentChatsByAssignmentId = await listAssignmentChatsMap({
                    assignmentIds: trackedAssignmentIds,
                    courseId,
                    ownerAccountId,
                });

                if (!isMounted) return;
                setAssignmentChatsByAssignmentId(nextAssignmentChatsByAssignmentId);
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
            }
        };

        loadAssignmentChatsMap();

        return () => {
            isMounted = false;
        };
    }, [courseId, ownerAccountId, trackedAssignmentIds]);

    useCompletedChatMessage(refreshAssignmentChat);

    useChatSummariesReconciliation({
        chats: assignmentChatsByAssignmentId ? Object.values(assignmentChatsByAssignmentId) : [],
        enabled: Boolean(assignmentChatsByAssignmentId),
        onOutOfSync: async (chatId) => {
            const currentAssignmentChat = Object.values(assignmentChatsByAssignmentId || {}).find(
                (assignmentChat) => assignmentChat?.id === chatId
            );
            if (!currentAssignmentChat?.assignment_id) return;

            await refreshAssignmentChat({
                assignment_id: currentAssignmentChat.assignment_id,
            });
        },
    });

    return {
        assignmentChatsByAssignmentId,
    };
};

export default useAssignmentChatsMap;
